import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import WordCloud, { Word } from 'react-wordcloud';
import Tooltip from './Tooltip';
import { ChartWrapper } from '../styles';

interface WordCloudChartProps {
  data: Word[];
}

const chartSize: [number, number] = [1031, 400];

interface StaticWordCloudProps {
  words: Word[];
  options: any;
  callbacks: any;
  size: [number, number];
}

const StaticWordCloud: React.FC<StaticWordCloudProps> = memo(
  ({ words, options, callbacks, size }) => (
    <WordCloud words={words} options={options} callbacks={callbacks} size={size} />
  ),
  (prev, next) => {
    return (
      prev.words === next.words &&
      prev.options === next.options &&
      prev.callbacks === next.callbacks &&
      prev.size[0] === next.size[0] &&
      prev.size[1] === next.size[1]
    );
  },
);

const WordCloudChart: React.FC<WordCloudChartProps> = ({ data }) => {
  const [hoveredWord, setHoveredWord] = useState<Word | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const options = useMemo(
    () => ({
      colors: ['#A54DD7'],
      rotations: 0,
      fontFamily: 'Sans-serif',
      fontSizes: [40, 120] as [number, number],
      padding: 5,
    }),
    [],
  );

  const showTooltip = useCallback((word: Word, event: MouseEvent) => {
    setHoveredWord(word);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
    setTooltipVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const callbacks = useMemo(
    () => ({
      onWordMouseOver: (word: Word, event?: MouseEvent) => {
        if (event) showTooltip(word, event);
      },
      onWordMouseOut: () => {
        hideTooltip();
      },
      getWordTooltip: () => '',
    }),
    [showTooltip, hideTooltip],
  );

  const formattedWords: Word[] = useMemo(
    () =>
      data.map(word => ({
        ...word,
        text: word.text.charAt(0).toUpperCase() + word.text.slice(1),
      })),
    [data],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    // 🛡️ Evita erro em SSR
    if (typeof window === 'undefined' || !containerRef.current) return;

    const updateSize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      setChartSize([width, Math.round(width * 0.4)]);
    };

    if (typeof (window as any).ResizeObserver !== 'undefined') {
      const observer = new (window as any).ResizeObserver(() => updateSize());
      observer.observe(containerRef.current!);
      updateSize(); // inicializa tamanho

      return () => observer.disconnect();
    } else {
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
  }, []);

  return (
    <ChartWrapper
      ref={containerRef}
      style={{
        width: '100%',
        height: 'auto',
        minHeight: 300,
        position: 'relative',
      }}
    >
      {chartSize[0] > 0 && (
        <StaticWordCloud
          words={formattedWords}
          options={options}
          callbacks={callbacks}
          size={chartSize}
        />
      )}
      <Tooltip
        word={hoveredWord}
        x={tooltipPosition.x}
        y={tooltipPosition.y}
        visible={tooltipVisible}
      />
    </ChartWrapper>
  );
};

const comparisonFn = (
  prevProps: WordCloudChartProps,
  newProps: WordCloudChartProps,
): boolean => {
  return JSON.stringify(prevProps) === JSON.stringify(newProps);
};

export default memo(WordCloudChart, comparisonFn);